import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, FormGroup, Input, Label, Row, Spinner } from 'reactstrap';
import Select from 'react-select';
import axios from '../../utils/axiosConfig';
import Swal from 'sweetalert2';
import '../../Pages/Css/correccionSolicitud.css';

const EMPTY_FORM = {
    contrato: '', tipoDocumento: '', documento: '', placa: '', tipoUnidad: '',
    destinoAutorizado: '', cliente: '', ruta: '', tipoContrato: '', servicio: '',
    categoriaServicio: '', causalCategoria: '',
};

const EMPTY_LISTS = {
    tiposDocumento: [], unidades: [], destinos: [], categorias: [], clientes: [],
    rutas: [], tiposContrato: [], servicios: [],
};

const firstValue = (source, keys, fallback = '') => {
    for (const key of keys) {
        const value = source?.[key];
        if (value !== undefined && value !== null) return String(value);
    }
    return fallback;
};

const option = (item, valueKeys, labelKeys) => ({
    value: firstValue(item, valueKeys),
    label: firstValue(item, labelKeys, '-'),
});

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '38px',
        borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(13, 110, 253, 0.25)' : 'none',
        '&:hover': { borderColor: '#86b7fe' },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
};

const ModalCorreccionSolicitud = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [lists, setLists] = useState(EMPTY_LISTS);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const lastLoadedContract = useRef('');

    const getList = async (url) => {
        const response = await axios.get(`${process.env.REACT_APP_SERVER}${url}`);
        if (response.data?.success === false) throw new Error(response.data?.message || `Error consultando ${url}`);
        return Array.isArray(response.data?.data) ? response.data.data : [];
    };

    const postList = async (url, body) => {
        const response = await axios.post(`${process.env.REACT_APP_SERVER}${url}`, body);
        if (response.data?.success === false) throw new Error(response.data?.message || `Error consultando ${url}`);
        return Array.isArray(response.data?.data) ? response.data.data : [];
    };

    useEffect(() => {
        const contract = form.contrato.trim().toUpperCase();
        if (!/^SERV-\d{8}$/.test(contract) || lastLoadedContract.current === contract) return;

        let active = true;
        const loadContract = async () => {
            setLoading(true);
            try {
                const [contractResponse, unidades, tiposDocumento, categorias, clientes] = await Promise.all([
                    axios.post(`${process.env.REACT_APP_SERVER}/contratos/getinfocontrato`, { contrato: contract }),
                    getList('/solicitudes/getlistaUnidadCarga'),
                    getList('/contratos/gettipodocumento'),
                    getList('/solicitudes/getcategoriasservicios'),
                    getList('/solicitudes/getlistaempresas'),
                ]);
                if (contractResponse.data?.success === false) throw new Error(contractResponse.data?.message || 'No fue posible consultar el contrato.');
                const info = Array.isArray(contractResponse.data?.data) ? contractResponse.data.data[0] : contractResponse.data?.data;
                if (!info) throw new Error('No se encontró información para el contrato ingresado.');

                const cliente = firstValue(info, ['FKICEmpresa', 'IdEmpresa']);
                const ruta = firstValue(info, ['FKICRutas', 'FKICRuta']);
                const tipoContrato = firstValue(info, ['FKICClienteExterno', 'IdClienteExterno', 'FKTipoContrato'], '0');
                const [rutas, destinos] = cliente ? await Promise.all([
                    postList('/solicitudes/getlistaRutasNegociadas', { empresa: cliente }),
                    postList('/solicitudes/getlistaGeocercasEmpresa', { empresa: cliente }),
                ]) : [[], []];
                const tiposContrato = cliente && ruta
                    ? await postList('/solicitudes/getlistaNegociaciones', { empresa: cliente, ruta }) : [];
                const servicios = cliente && ruta
                    ? await postList('/solicitudes/getlistaNegociacionesFinal', {
                        empresa: cliente, ruta, externo: tipoContrato,
                    }) : [];
                console.log('Servcios obtenidos para corrección de solicitud:', servicios);

                if (!active) return;
                lastLoadedContract.current = contract;
                setLists({ tiposDocumento, unidades, destinos, categorias, clientes, rutas, tiposContrato, servicios });

                setForm({
                    contrato: contract,
                    tipoDocumento: firstValue(info, ['FKLokTipoDocumento', 'FKTipoDocumento', 'TipoDocumentoID']),
                    documento: firstValue(info, ['Documento', 'NroDocumento', 'NumeroDocumento']),
                    placa: firstValue(info, ['PlacaTruck', 'Placa']),
                    tipoUnidad: firstValue(info, ['FKLokTipoUnidadCarga', 'TipoUnidadCargaID']),
                    destinoAutorizado: firstValue(info, ['FKCercaAutorizada', 'FKLokCercaAutorizada']),
                    cliente,
                    ruta,
                    tipoContrato,
                    servicio: firstValue(info, ['IDNegociacion', 'FKLokServicio']),
                    categoriaServicio: firstValue(info, ['FKLokCategoriaServ', 'IdCategoriaServ']),
                    causalCategoria: firstValue(info, ['NotasTI', 'CausalCategoria']),
                });
                console.log('Información cargada para corrección de solicitud:', info);
            } catch (error) {
                if (active) Swal.fire('Error', error.response?.data?.message || error.message || 'Error cargando la solicitud.', 'error');
            } finally {
                if (active) setLoading(false);
            }
        };
        const timer = setTimeout(loadContract, 300);
        return () => { active = false; clearTimeout(timer); };
    }, [form.contrato]);

    const updateField = (event) => {
        const { name, value } = event.target;
        setForm((current) => ({ ...current, [name]: name === 'contrato' ? value.toUpperCase() : value }));
    };

    const changeClient = async (event) => {
        const cliente = event.target.value;
        setForm((current) => ({ ...current, cliente, ruta: '', tipoContrato: '', servicio: '', destinoAutorizado: '' }));
        setLists((current) => ({ ...current, rutas: [], tiposContrato: [], servicios: [], destinos: [] }));
        if (!cliente) return;
        try {
            const [rutas, destinos] = await Promise.all([
                postList('/solicitudes/getlistaRutasNegociadas', { empresa: cliente }),
                postList('/solicitudes/getlistaGeocercasEmpresa', { empresa: cliente }),
            ]);
            setLists((current) => ({ ...current, rutas, destinos }));
        } catch (error) { Swal.fire('Error', error.message, 'error'); }
    };

    const changeRoute = async (event) => {
        const ruta = event.target.value;
        setForm((current) => ({ ...current, ruta, tipoContrato: '', servicio: '' }));
        setLists((current) => ({ ...current, tiposContrato: [], servicios: [] }));
        if (!form.cliente || !ruta) return;
        try {
            const tiposContrato = await postList('/solicitudes/getlistaNegociaciones', {
                empresa: form.cliente, ruta,
            });
            const firstContractType = firstValue(tiposContrato[0], ['IdClienteExterno'], '0');
            const servicios = await postList('/solicitudes/getlistaNegociacionesFinal', {
                empresa: form.cliente,
                ruta,
                externo: firstContractType,
            });
            setLists((current) => ({ ...current, tiposContrato, servicios }));
            setForm((current) => ({
                ...current,
                tipoContrato: firstContractType,
                servicio: firstValue(servicios[0], ['IDNegociacion']),
            }));
        } catch (error) { Swal.fire('Error', error.message, 'error'); }
    };

    const changeContractType = async (event) => {
        const tipoContrato = event.target.value;
        setForm((current) => ({ ...current, tipoContrato, servicio: '' }));
        setLists((current) => ({ ...current, servicios: [] }));
        if (!form.cliente || !form.ruta || tipoContrato === '') return;
        try {
            const servicios = await postList('/solicitudes/getlistaNegociacionesFinal', {
                empresa: form.cliente, ruta: form.ruta, externo: tipoContrato,
            });
            setLists((current) => ({ ...current, servicios }));
            setForm((current) => ({
                ...current,
                servicio: firstValue(servicios[0], ['IDNegociacion']),
            }));
        } catch (error) { Swal.fire('Error', error.message, 'error'); }
    };

    const submit = async (event) => {
        event.preventDefault();
        if (!/^SERV-\d{8}$/.test(form.contrato.trim())) {
            Swal.fire('Información', 'Ingrese un contrato válido con el formato SERV-########.', 'info');
            return;
        }

        const toNumberOrNull = (value) => value === '' || value === null ? null : Number(value);
        const payload = {
            Contrato: form.contrato.trim(),
            Empresa: toNumberOrNull(form.cliente),
            ClienteExt: form.tipoContrato === '' ? 0 : Number(form.tipoContrato),
            Ruta: toNumberOrNull(form.ruta),
            IdNegocio: toNumberOrNull(form.servicio),
            notasTI: form.causalCategoria.trim(),
            FKLokCategoriaServ: toNumberOrNull(form.categoriaServicio),
            documento: form.documento.trim(),
            cercaAut: toNumberOrNull(form.destinoAutorizado),
            placa: form.placa.trim(),
            tipoDoc: toNumberOrNull(form.tipoDocumento),
            tipoUniCar: toNumberOrNull(form.tipoUnidad),
        };

        console.log('Payload corrección contrato:', payload);
        setSaving(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_SERVER}/contratos/correccioncontrato`,
                payload
            );
            console.log('Respuesta corrección contrato:', response.data);
            if (response.data?.success === false) {
                throw new Error(response.data?.message || response.data?.mensaje || 'No fue posible corregir el contrato.');
            }
            Swal.fire('¡Éxito!', 'El contrato se corrigió correctamente.', 'success');
        } catch (error) {
            Swal.fire(
                'Error',
                error.response?.data?.message || error.response?.data?.mensaje || error.message || 'Error corrigiendo el contrato.',
                'error'
            );
        } finally {
            setSaving(false);
        }
    };

    const renderInput = (name, label, type = 'text', options = [], onChange = updateField) => {
        const inputId = `correction-request-${name}`;
        const selectOptions = [
            { value: name === 'tipoContrato' ? '0' : '', label: '- No asignado -' },
            ...options.filter((item) => item.value !== ''),
        ];
        const selectedOption = selectOptions.find((item) => String(item.value) === String(form[name])) || null;
        return <FormGroup><Label for={inputId}>{label}</Label>{type === 'select' ? (
            <Select
                inputId={inputId}
                classNamePrefix="request-correction-select"
                options={selectOptions}
                value={selectedOption}
                onChange={(selected) => onChange({ target: { name, value: selected?.value || '' } })}
                placeholder="Buscar o seleccionar..."
                noOptionsMessage={() => 'Sin coincidencias'}
                isClearable
                isSearchable
                isDisabled={loading}
                styles={selectStyles}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                menuPosition="fixed"
            />
        ) : <Input id={inputId} name={name} type={type} value={form[name]} onChange={onChange}
            disabled={loading && name !== 'contrato'} {...(type === 'textarea' ? { rows: 3 } : {})} />}</FormGroup>;
    };

    const mapped = {
        tiposDocumento: lists.tiposDocumento.map((x) => option(x, ['IDTipoDocumento', 'IdTipoDocumento', 'TipoDocumentoID', 'ID'], ['Descripcion', 'TipoDocumento', 'Nombre'])),
        unidades: lists.unidades.map((x) => option(x, ['TipoUnidadCargaID'], ['DescripcionTipoCarga'])),
        destinos: lists.destinos.map((x) => option(x, ['ID'], ['Nombre'])),
        categorias: lists.categorias.map((x) => option(x, ['IdCategoriaServ'], ['CategoriaServ'])),
        clientes: lists.clientes.map((x) => option(x, ['IdEmpresa'], ['NombreEmpresa'])),
        rutas: lists.rutas.map((x) => option(x, ['FKICRuta'], ['DescripcionRuta'])),
        tiposContrato: lists.tiposContrato
            .map((x) => option(x, ['IdClienteExterno'], ['Descripcion']))
            .filter((x) => x.value !== '0'),
        servicios: lists.servicios.map((x) => option(x, ['IDNegociacion'], ['Descripcion'])),
    };

    return <form className="request-correction-form" onSubmit={submit}>
        <div className="request-correction-contract">
            {renderInput('contrato', 'Contrato')}
            {loading && <span className="request-correction-loading"><Spinner size="sm" /> Cargando información...</span>}
            {!loading && (
                <div className="request-correction-info" role="note">
                    <i className="bx bx-info-circle" aria-hidden="true"></i>
                    <span>
                        Ingrese un contrato completo con el formato <strong>SERV-########</strong>.
                        Si el contrato está incompleto o no existe, no se podrá cargar el resto de la información.
                    </span>
                </div>
            )}
        </div>
        <Row><Col md="6">
            {renderInput('tipoDocumento', 'Tipo documento', 'select', mapped.tiposDocumento)}
            {renderInput('documento', 'Documento')}{renderInput('placa', 'Placa')}
            {renderInput('tipoUnidad', 'Tipo unidad', 'select', mapped.unidades)}
            {renderInput('destinoAutorizado', 'Destino autorizado', 'select', mapped.destinos)}
        </Col><Col md="6">
                {renderInput('cliente', 'Cliente', 'select', mapped.clientes, changeClient)}
                {renderInput('ruta', 'Ruta', 'select', mapped.rutas, changeRoute)}
                {renderInput('tipoContrato', 'Tipo contrato', 'select', mapped.tiposContrato, changeContractType)}
                {renderInput('servicio', 'Servicio', 'select', mapped.servicios)}
                {renderInput('categoriaServicio', 'Categoría servicio', 'select', mapped.categorias)}
            </Col></Row>
        {renderInput('causalCategoria', 'Causal por categoría', 'textarea')}
        <div className="request-correction-actions">
            <Button type="submit" className="request-correction-primary" disabled={loading || saving}>
                {saving ? <><Spinner size="sm" /> Guardando...</> : 'Grabar'}
            </Button>
        </div>
    </form>;
};

export default ModalCorreccionSolicitud;
